<template>
  <div class="progressive-image-container" :style="containerStyle">
    <div class="tiles-wrapper" :style="wrapperStyle">
      <img
        v-for="tile in loadedTiles"
        :key="`tile-${tile.x}-${tile.y}`"
        :data-tile-index="tile.index"
        :src="tile.url"
        :style="getTileStyle(tile)"
        class="tile-image"
        @load="onTileLoad"
        @error="onTileError"
      />
    </div>

    <div v-if="loading" class="loading-overlay" :style="loadingOverlayStyle">
      <div class="loading-content">
        <div class="loading-text">
          加载中... {{ loadedCount }}/{{ totalTiles }}
        </div>
        <div class="loading-progress">
          <a-progress
            :percent="loadProgress"
            :stroke-width="4"
            :showInfo="false"
          />
          <span class="progress-text">{{ loadProgress }}%</span>
        </div>
        <div class="loading-dots">
          <span class="dot" :class="{ active: dotActive === 1 }"></span>
          <span class="dot" :class="{ active: dotActive === 2 }"></span>
          <span class="dot" :class="{ active: dotActive === 3 }"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, defineProps, defineEmits, defineExpose } from 'vue'

const props = defineProps({
  tileId: {
    type: String,
    required: true
  },
  path: {
    type: String,
    required: true
  },
  originalWidth: {
    type: Number,
    required: true
  },
  originalHeight: {
    type: Number,
    required: true
  },
  tileWidth: {
    type: Number,
    default: 512
  },
  tileHeight: {
    type: Number,
    default: 512
  },
  tilesX: {
    type: Number,
    required: true
  },
  tilesY: {
    type: Number,
    required: true
  },
  tileUrls: {
    type: Array,
    default: null
  },
  containerWidth: {
    type: [Number, String],
    default: null
  },
  autoLoad: {
    type: Boolean,
    default: true
  },
  loadOrder: {
    type: String,
    default: 'row',
    validator: (value) => ['row', 'col', 'diagonal', 'random'].includes(value)
  }
})

const emit = defineEmits(['load-complete', 'tile-loaded', 'tile-error'])

// 响应式数据
const tiles = ref([])
const loadedTiles = ref([])
const loading = ref(false)
const loadedCount = ref(0)
const currentLoadIndex = ref(0)
const dotActive = ref(1)
let dotInterval = null

// 计算属性
const totalTiles = computed(() => props.tilesX * props.tilesY)

const loadProgress = computed(() => {
  return totalTiles.value > 0
    ? Math.round((loadedCount.value / totalTiles.value) * 100)
    : 0
})

const containerStyle = computed(() => {
  const width = props.containerWidth || props.originalWidth
  const aspectRatio = props.originalHeight / props.originalWidth

  if (props.containerWidth && typeof props.containerWidth === 'string') {
    const paddingTopPercent = aspectRatio * 100
    return {
      width: props.containerWidth,
      paddingTop: `${paddingTopPercent}%`,
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: '#f5f5f5',
      borderRadius: '4px'
    }
  }

  const height = typeof width === 'number' ? width * aspectRatio : `${width}px`

  return {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  }
})

const wrapperStyle = computed(() => {
  if (props.containerWidth && typeof props.containerWidth === 'string') {
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%'
    }
  }

  const scaleX = props.containerWidth
    ? (typeof props.containerWidth === 'number' ? props.containerWidth / props.originalWidth : 1)
    : 1
  const scaleY = props.containerWidth
    ? (typeof props.containerWidth === 'number' ? props.containerWidth / props.originalWidth : 1)
    : 1

  return {
    width: `${props.originalWidth}px`,
    height: `${props.originalHeight}px`,
    transform: `scale(${scaleX}, ${scaleY})`,
    transformOrigin: 'top left',
    position: 'relative'
  }
})

const loadingOverlayStyle = computed(() => {
  if (props.containerWidth && typeof props.containerWidth === 'string') {
    return {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    }
  }
  return {}
})

// 监听器
watch(() => props.tileUrls, (newUrls) => {
  if (newUrls && newUrls.length > 0) {
    initTiles(newUrls)
  }
}, { immediate: true })

watch(() => props.autoLoad, (newVal) => {
  if (newVal && tiles.value.length > 0) {
    startLoading()
  }
}, { immediate: true })

// 生命周期
onMounted(() => {
  if (props.tileUrls && props.tileUrls.length > 0) {
    initTiles(props.tileUrls)
  }
})

onUnmounted(() => {
  if (dotInterval) {
    clearInterval(dotInterval)
  }
})

// 方法
function initTiles (urls) {
  tiles.value = []
  let index = 0
  for (let y = 0; y < props.tilesY; y++) {
    for (let x = 0; x < props.tilesX; x++) {
      const url = urls[index]
      if (url) {
        tiles.value.push({
          url,
          x,
          y,
          index,
          loaded: false,
          error: false
        })
      }
      index++
    }
  }

  sortTilesByLoadOrder()

  if (props.autoLoad) {
    startLoading()
  }
}

function sortTilesByLoadOrder () {
  switch (props.loadOrder) {
    case 'col':
      tiles.value.sort((a, b) => {
        if (a.x !== b.x) return a.x - b.x
        return a.y - b.y
      })
      break
    case 'diagonal':
      tiles.value.sort((a, b) => {
        const distA = a.x + a.y
        const distB = b.x + b.y
        if (distA !== distB) return distA - distB
        return a.y - b.y
      })
      break
    case 'random':
      tiles.value = [...tiles.value].sort(() => Math.random() - 0.5)
      break
    case 'row':
    default:
      break
  }
}

function startLoading () {
  if (tiles.value.length === 0) return

  loading.value = true
  loadedCount.value = 0
  currentLoadIndex.value = 0
  loadedTiles.value = []

  // 启动加载指示器动画
  startDotAnimation()

  // 开始加载第一个分片
  loadNextTile()
}

function loadNextTile () {
  if (currentLoadIndex.value >= tiles.value.length) {
    loading.value = false
    stopDotAnimation()
    emit('load-complete', loadedCount.value)
    return
  }

  const tile = tiles.value[currentLoadIndex.value]
  if (tile && tile.url) {
    loadedTiles.value.push(tile)
    currentLoadIndex.value++
  } else {
    currentLoadIndex.value++
    loadNextTile()
  }
}

function onTileLoad (event) {
  const img = event.target
  const tileIndex = parseInt(img.getAttribute('data-tile-index'), 10)
  const tile = loadedTiles.value.find(t => t.index === tileIndex)

  if (!tile || tile.loaded) return

  tile.loaded = true
  loadedCount.value++
  emit('tile-loaded', { tile, loadedCount: loadedCount.value, totalTiles: totalTiles.value })

  loadNextTile()
}

function onTileError (event) {
  const img = event.target
  const tileIndex = parseInt(img.getAttribute('data-tile-index'), 10)
  const tile = loadedTiles.value.find(t => t.index === tileIndex)

  if (tile) {
    tile.error = true
    tile.loaded = false
    console.error('分片加载失败:', tile)
    emit('tile-error', tile)
  }

  loadNextTile()
}

function getTileStyle (tile) {
  const baseStyle = {
    position: 'absolute',
    display: tile.error ? 'none' : 'block',
    opacity: tile.loaded ? 1 : 0,
    transition: 'opacity 0.3s ease-in-out'
  }

  if (props.containerWidth && typeof props.containerWidth === 'string') {
    const leftPercent = Number((tile.x * props.tileWidth / props.originalWidth * 100).toFixed(6))
    const topPercent = Number((tile.y * props.tileHeight / props.originalHeight * 100).toFixed(6))
    const actualTileWidth = Math.min(props.tileWidth, props.originalWidth - tile.x * props.tileWidth)
    const actualTileHeight = Math.min(props.tileHeight, props.originalHeight - tile.y * props.tileHeight)
    const widthPercent = Number((actualTileWidth / props.originalWidth * 100).toFixed(6))
    const heightPercent = Number((actualTileHeight / props.originalHeight * 100).toFixed(6))

    return {
      ...baseStyle,
      left: `${leftPercent}%`,
      top: `${topPercent}%`,
      width: `${widthPercent}%`,
      height: `${heightPercent}%`
    }
  }

  const left = tile.x * props.tileWidth
  const top = tile.y * props.tileHeight
  const width = Math.min(props.tileWidth, props.originalWidth - left)
  const height = Math.min(props.tileHeight, props.originalHeight - top)

  return {
    ...baseStyle,
    left: `${left}px`,
    top: `${top}px`,
    width: `${width}px`,
    height: `${height}px`
  }
}

function startDotAnimation () {
  dotInterval = setInterval(() => {
    dotActive.value = dotActive.value % 3 + 1
  }, 400)
}

function stopDotAnimation () {
  if (dotInterval) {
    clearInterval(dotInterval)
    dotInterval = null
  }
  dotActive.value = 1
}

defineExpose({
  reload: startLoading
})
</script>

<style scoped lang="scss">
.progressive-image-container {
  position: relative;
  background-color: var(--bg-color, #f5f5f5);
  transition: background-color 0.3s ease;

  &:hover {
    --bg-color: #f0f0f0;
  }

  .tiles-wrapper {
    position: relative;
    width: 100%;
    height: 100%;

    .tile-image {
      display: block;
      image-rendering: -webkit-optimize-contrast;
      image-rendering: crisp-edges;
      will-change: opacity;

      &:hover {
        filter: brightness(1.05);
        transition: filter 0.2s ease;
      }
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 20;
    background: linear-gradient(135deg,
      rgba(255, 255, 255, 0.45) 0%,
      rgba(248, 250, 252, 0.45) 100%);
    backdrop-filter: blur(1px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    animation: fadeIn 0.3s ease;

    .loading-content {
      text-align: center;
      padding: 24px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      min-width: 280px;

      .loading-text {
        margin-bottom: 16px;
        font-size: 15px;
        color: var(--text-primary, #1f2937);
        font-weight: 500;
        letter-spacing: 0.3px;
      }

      .loading-progress {
        margin-bottom: 20px;
        position: relative;

        :deep(.ant-progress) {
          width: 200px;
          margin: 0 auto;

          .el-progress-bar {
            padding-right: 0;
          }

          .el-progress-bar__outer {
            background-color: var(--progress-bg, #e5e7eb);
            border-radius: 10px;
            overflow: hidden;

            .el-progress-bar__inner {
              background: linear-gradient(90deg,
                var(--progress-start, #3b82f6) 0%,
                var(--progress-end, #8b5cf6) 100%);
              transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
              border-radius: 10px;
            }
          }
        }

        .progress-text {
          position: absolute;
          right: -20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 13px;
          color: var(--text-secondary, #6b7280);
          font-weight: 600;
          min-width: 36px;
        }
      }

      .loading-dots {
        display: flex;
        justify-content: center;
        gap: 8px;

        .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: var(--dot-inactive, #d1d5db);
          transition: all 0.3s ease;
          opacity: 0.6;

          &.active {
            background-color: var(--dot-active, #3b82f6);
            transform: scale(1.2);
            opacity: 1;
            animation: pulse 1.5s ease-in-out infinite;
          }

          &:nth-child(1).active {
            animation-delay: 0s;
          }
          &:nth-child(2).active {
            animation-delay: 0.2s;
          }
          &:nth-child(3).active {
            animation-delay: 0.4s;
          }
        }
      }
    }
  }
}

// 动画定义
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1.2);
  }
  50% {
    opacity: 0.7;
    transform: scale(1);
  }
}

// 响应式调整
@media (max-width: 640px) {
  .progressive-image-container {
    .loading-overlay {
      .loading-content {
        min-width: 240px;
        padding: 20px;

        .loading-progress {
          :deep(.el-progress) {
            width: 160px;
          }

          .progress-text {
            right: -30px;
            font-size: 12px;
          }
        }

        .loading-text {
          font-size: 14px;
        }
      }
    }
  }
}

// 暗色模式支持
@media (prefers-color-scheme: dark) {
  .progressive-image-container {
    --bg-color: #1f2937;
    --text-primary: #f9fafb;
    --text-secondary: #d1d5db;
    --progress-bg: #374151;
    --progress-start: #60a5fa;
    --progress-end: #a78bfa;
    --dot-inactive: #4b5563;
    --dot-active: #60a5fa;

    .loading-overlay {
      background: linear-gradient(135deg,
        rgba(31, 41, 55, 0.95) 0%,
        rgba(17, 24, 39, 0.95) 100%);

      .loading-content {
        background: #111827;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      }
    }
  }
}
</style>
